import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/db/client";
import type { FilterPreset } from "@/types/filterPresets";
import { PRESETS_STORAGE_KEY, MAX_PRESETS } from "@/types/filterPresets";

export const useFilterPresets = () => {
  const queryClient = useQueryClient();

  // Resolve auth state once on mount
  const [userId, setUserId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id ?? null);
    });
  }, []);

  const isAuthenticated = !!userId;

  // --- DB path (authenticated users) ---

  const { data: dbPresets = [] } = useQuery<FilterPreset[]>({
    queryKey: ["filter-presets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("filter_presets")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data || []).map((row) => ({
        id: row.id,
        name: row.name,
        filters: row.filters as FilterPreset["filters"],
        createdAt: row.created_at,
      }));
    },
    enabled: isAuthenticated,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async ({ name, filters }: { name: string; filters: FilterPreset["filters"] }) => {
      const { error } = await supabase.from("filter_presets").insert({ user_id: userId!, name, filters });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["filter-presets"] }),
    onError: () => toast.error("Failed to save preset"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("filter_presets").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["filter-presets"] }),
    onError: () => toast.error("Failed to delete preset"),
  });

  const renameMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase.from("filter_presets").update({ name }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["filter-presets"] }),
    onError: () => toast.error("Failed to rename preset"),
  });

  // --- localStorage path (unauthenticated) ---

  const [localPresets, setLocalPresets] = useState<FilterPreset[]>(() => {
    try {
      const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const persistLocal = useCallback((updated: FilterPreset[]) => {
    setLocalPresets(updated);
    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      /* localStorage unavailable (private mode / quota) — presets stay in memory */
    }
  }, []);

  // --- One-time migration: localStorage → DB on first authenticated load ---

  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
    if (!stored) return;

    let local: FilterPreset[];
    try {
      local = JSON.parse(stored);
    } catch {
      localStorage.removeItem(PRESETS_STORAGE_KEY);
      return;
    }

    if (local.length === 0) {
      localStorage.removeItem(PRESETS_STORAGE_KEY);
      return;
    }

    supabase
      .from("filter_presets")
      .insert(
        local.map((p) => ({
          id: p.id,
          user_id: userId,
          name: p.name,
          filters: p.filters,
          created_at: p.createdAt,
        })),
      )
      .then(({ error }) => {
        if (!error) {
          localStorage.removeItem(PRESETS_STORAGE_KEY);
          setLocalPresets([]);
          queryClient.invalidateQueries({ queryKey: ["filter-presets"] });
        }
      });
  }, [isAuthenticated, userId, queryClient]);

  // --- Unified interface ---

  const presets = isAuthenticated ? dbPresets : localPresets;

  const savePreset = useCallback(
    (name: string, filters: FilterPreset["filters"]) => {
      if (presets.length >= MAX_PRESETS) return false;

      if (isAuthenticated) {
        createMutation.mutate({ name, filters });
      } else {
        persistLocal([
          ...localPresets,
          { id: crypto.randomUUID(), name, filters, createdAt: new Date().toISOString() },
        ]);
      }
      return true;
    },
    [presets.length, isAuthenticated, localPresets, createMutation, persistLocal],
  );

  const deletePreset = useCallback(
    (id: string) => {
      if (isAuthenticated) {
        deleteMutation.mutate(id);
      } else {
        persistLocal(localPresets.filter((p) => p.id !== id));
      }
    },
    [isAuthenticated, localPresets, deleteMutation, persistLocal],
  );

  const renamePreset = useCallback(
    (id: string, name: string) => {
      if (isAuthenticated) {
        renameMutation.mutate({ id, name });
      } else {
        persistLocal(localPresets.map((p) => (p.id === id ? { ...p, name } : p)));
      }
    },
    [isAuthenticated, localPresets, renameMutation, persistLocal],
  );

  return { presets, savePreset, deletePreset, renamePreset };
};
